/* istanbul ignore file */
import React, { useCallback, useMemo, useState } from 'react';
import { ApolloError } from '@apollo/client';
import { Box, xcss } from '@atlaskit/primitives';
import Heading from '@atlaskit/heading';
import classnames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { get, groupBy, isEqual, uniqBy } from 'lodash';
import { AutoDismissFlag, FlagGroup } from '@atlaskit/flag';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import OfficeBuildingFilledIcon from '@atlaskit/icon/glyph/office-building-filled';
import {
  OrderSummaryLineItems,
  LineItemsActualEstimate,
  OrderSummaryAppLineItem,
  BillingProfileActionsEnum,
} from './types';
import {
  Divider,
  useApplicationContext,
  MoreActions,
  MoreActionsItem,
  useNotification,
  successNotificationBuilder,
  NotificationTypes,
  errorNotificationBuilder,
  useFeatureToggle,
  InfoPanel,
} from '@css-xp/base';
import {
  CommerceAddress,
  EndCustomerContact,
  InvoiceGroup,
  PaymentMethod,
  Currency,
  ContactInfo,
} from '@css-xp/graphtypes/src/generated-types-new';
import { createContactRelationship, patchContactRelationship, updateInvoiceGroup } from '@atlassian/temp-proxies';
import { UpdateInvoiceGroupRequest } from '@atlassian/cssxp-types-billing';
import { PartnerAccount } from '@css-xp/ppc-types';
import { CCPPricing } from '@atlassian/cssxp-typescommon';
import { getOrderPreviewV2, updateEntitlementPlanV2 } from '@atlassian/cssxp-proxies';
import {
  AnalyticsTrackAction,
  AnalyticsTrackSubject,
  PPCAnalyticsTrackSubject,
  sendAnalyticsEvent,
} from '@atlassian/temp-analytics';
import Button, { LoadingButton } from '@atlaskit/button';
import { ValueType } from '@atlaskit/select';
import { useOpenClose } from '@css-xp/ppc-custom-hooks';
import {
  formatErrorMessage,
  getMaxLimitOfCustomerContact,
  isPaypalPaymentMethod,
  sortBillingProfiles,
} from '@css-xp/ppc-utils';
import Form, { CheckboxField, ErrorMessage } from '@atlaskit/form';
import { Checkbox } from '@atlaskit/checkbox';
import { FormatCommerceAddress, getSupportUrl } from '@css-xp/ppc-common';
import { SkeletonLoader, SkeletonLoaderWrapper } from '@css-xp/skeleton-loader';
import { useConsentHub } from '@css-xp/ppc-consent-hub';
import { ADVOCATE_ACCESS_TYPE, ADVOCATE_RESOURCE_TYPE, AdvocateAccessWall } from '@css-xp/ppc-access-control';
import { useContactServicePayload, useFetchEccFromContactService } from '@css-xp/contact-resources';
import {
  CreateContactRelationshipsRequest,
  PatchContactRelationshipsRequest,
} from '@css-xp/contact-resources/src/constant';
import { STATSIG_FEATURE_GATES } from '@css-xp/ppc-constants';
import { BillingProfileAndPONumberModal } from './billing-profile-po-number-modal';
import { BillingProfile } from './billing-profile';
import type { CartProductSchema, ValidationErrorSchema } from '../../../../types';
import { CloudSiteTypeEnum, OperationType, ProductStatus } from '../../../../types';
import type { State } from '../../../../store/types';
import {
  useIsSandboxLinkedEntitlementsInCart,
  useDestinationBillingProfileDetails,
  useOrgProductInstancesMap,
} from '../../../../store';
import { CurrentScreenTypeEnum } from '../../../../store/types';
import { getEntitlementIdForItemId, getOmsPayload, OmsCapability } from '../../../../store/utils';
import { getBillCoupledItemsForIGChange } from '../../../../store/utils/get-bill-coupled-items-for-ig-change';
import {
  CustomPanel,
  LineItemsCustomPanelV2,
  Estimate,
  OrderSummaryTotalEstimate,
} from '@css-xp/ppc-purchase-experience';
import { LineItemsCustomPanel } from './line-items-custom-panel';
import { NewCloudSiteLozenge } from '../../../product-line-items/src/components/product-line-item';
import { OrderConfirmationContainer } from './order-confirmation-container /order-confirmation-container';
import SectionMessage from '@atlaskit/section-message';
import { ChangePaymentMethodModal } from './change-payment-method-modal';
import { OrderDetailsProps } from '../../../../order-page';
import { totalEstimateStyle } from './order-summary.styles';
import './order-summary.scss';

export type CustomerContact = {
  account_id: string;
  name: string;
  email?: string;
  picture?: string;
};

export interface EccAccountDetails {
  loading: boolean;
  data: Array<CustomerContact>;
}

export type OrderSummaryProps = {
  lineItems: OrderSummaryLineItems;
  cartProducts: CartProductSchema[];
  cartApps: State['linkedEntitlements'];
  offeringsAndpricingPlans: State['offeringsAndpricingPlans'];
  nextBillDate: State['nextBillDate'];
  selectedProducts: State['selectedProducts'];
  partnerPromotions: State['partnerPromotions'];
  currentOperationTypes: OperationType[] | null;
  transactionAccountId: string;
  invoiceGroupId: string;
  invoiceGroupName?: string;
  lineItemsActualEstimate: LineItemsActualEstimate;
  billToAddressData: CommerceAddress;
  setSummaryDrawerOpen: (isOpen: boolean) => void;
  setValidationErrors: (validationErrors: ValidationErrorSchema[]) => void;
  setInvoiceGroupId: (selectedInvoiceGroupId: string) => void;
  products: State['products'];
  selectedLinkedEntitlements: State['selectedLinkedEntitlements'];
  existingCustomerContacts?: EccAccountDetails;
  isAddNewContactDisabled?: boolean;
  isExistingContactRemoveDisabled?: boolean;
  isCustomerContactHidden?: boolean;
  setCurrentScreen: (currentScreen: CurrentScreenTypeEnum) => void;
  availableBillingProfilesForCustomer?: InvoiceGroup[];
  isBillingProfilesForCustomerLoading?: boolean;
  refetchbillingProfilesForCustomer: () => void;
  isPaymentMethodNullForSelectedBillingProfile?: boolean;
  onResetOrder?: () => void;
  fetchBillingProfilesForCustomerError?: ApolloError;
  isIGChangeForFreeEntitlementEnabled?: boolean;
  removableCustomerEmailList?: string[];
  sandboxProducts: State['sandboxProducts'];
  sandboxApps: State['sandboxApps'];
  showSandboxStatus?: boolean;
  selectedPricingEligibility?: string;
  isNonCommercialPricingEnabled?: boolean;
  isMultiCurrencyChangesEnabled?: boolean;
  isJPDAsJiraFamilyEnabled?: boolean;
  isCoTermInUofEnabled?: boolean;
  isSandboxAppCreateInUOFEnabled?: boolean;
  sandboxParentData?: State['sandboxParentData'];
  isDesignIteration1Enabled?: boolean;
  isTWCPhase2Enabled?: boolean;
  setOrderDetails?: (_orderDetails: OrderDetailsProps) => void;
  isAppEditionSupportEnabled?: boolean;
};

interface orderItemResponse {
  itemId: string;
  processingInfo: {
    status: string;
  };
  errors: ValidationErrorSchema['errors'];
}

export interface BillingProfileOption {
  label: string;
  value: string;
}

const billingAddressStyle = xcss({
  width: '100%',
  boxSizing: 'border-box',
  border: '1px solid',
  borderColor: 'color.border',
  borderRadius: 'border.radius.200',
  padding: 'space.300',
  paddingBottom: '50px',
});

export const OrderSummary: React.FC<OrderSummaryProps> = ({
  lineItems,
  cartProducts,
  cartApps,
  nextBillDate,
  selectedProducts,
  partnerPromotions,
  currentOperationTypes,
  transactionAccountId,
  invoiceGroupId,
  invoiceGroupName,
  lineItemsActualEstimate,
  billToAddressData,
  offeringsAndpricingPlans,
  setSummaryDrawerOpen,
  setValidationErrors,
  setInvoiceGroupId,
  products,
  selectedLinkedEntitlements,
  // @ts-ignore
  existingCustomerContacts,
  isCustomerContactHidden = false,
  setCurrentScreen,
  availableBillingProfilesForCustomer = [],
  isBillingProfilesForCustomerLoading,
  refetchbillingProfilesForCustomer,
  isPaymentMethodNullForSelectedBillingProfile = false,
  onResetOrder,
  fetchBillingProfilesForCustomerError,
  isIGChangeForFreeEntitlementEnabled,
  removableCustomerEmailList = [],
  sandboxProducts,
  sandboxApps,
  showSandboxStatus = false,
  selectedPricingEligibility,
  isNonCommercialPricingEnabled = false,
  isMultiCurrencyChangesEnabled = false,
  isJPDAsJiraFamilyEnabled = false,
  isCoTermInUofEnabled = false,
  isSandboxAppCreateInUOFEnabled = false,
  sandboxParentData = {},
  isDesignIteration1Enabled = false,
  isTWCPhase2Enabled = false,
  setOrderDetails,
  isAppEditionSupportEnabled = false,
}) => {
  const orgProductInstancesMap = useOrgProductInstancesMap();
  const orgProductInstancesMapProp = isDesignIteration1Enabled
    ? { orgProductInstancesMap: orgProductInstancesMap }
    : {};
  const LineItems = isDesignIteration1Enabled ? LineItemsCustomPanelV2 : LineItemsCustomPanel;
  const context = useApplicationContext();
  const [isEnabled] = useFeatureToggle();
  const { isSandboxLinkedEntitlementsInCart, isRovoEntilementsInCart } = useIsSandboxLinkedEntitlementsInCart();
  const partnerAccounts = context.partnerAccounts;
  const relatedPartnerAccount = partnerAccounts?.find(
    (partnerAccount: PartnerAccount) => partnerAccount.transactionAccountId === transactionAccountId
  );
  const [omsOrderResponse, setOmsOrderResponse] = useState({});
  const [submitError, setSubmitError] = useState<{
    title?: string;
    description: string;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { updateCustomerConsent } = useConsentHub();
  const { sendSuccessNotification, setNotificationSent, sendErrorNotification, notificationSent } = useNotification();
  const { getECCDetails, eccDetailsError, getBulkECCDetails, bulkEccDetailsLoading } = useFetchEccFromContactService();
  const { getCreateContactRelationshipsPayload, getPatchPayloadForSetPrimaryContact } = useContactServicePayload();
  const billingProfileDetails = useDestinationBillingProfileDetails();
  const selectedCurrency = billingProfileDetails?.currency ?? 'USD';
  const [primaryContacts, setPrimaryContacts] = useState<ContactInfo[]>([]);
  const [hasExistingPrimaryContact, setHasExistingPrimaryContact] = useState(false);

  // grouping lineitems with cloud site id pair
  const groupedLineItemsByCloudSite = groupBy(
    lineItems?.filter((lineItem) => !lineItem?.organisationName),
    'existingCloudSite'
  );

  // grouping lineitems with organisation name
  const groupLineItemsByOrganisationName = groupBy(
    lineItems?.filter((lineItem) => !!lineItem?.organisationName),
    'organisationName'
  );
  const areAllTheAddedProductsFree = lineItems.every((lineItem) => lineItem.isFreeProduct);
  const isPrimaryContactsEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_CUSTOMER_CONTACT_V3);
  const isSupportForPayPalEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_PAYPAL_SUPPORT);
  const isAchPaymentMethodFFEnabled = isEnabled(STATSIG_FEATURE_GATES.ENABLE_ACH_DIRECT_DEBIT);
  // TODO :: enable this code when removal of ECC needs to be enabled again from PPC
  // const existingCustomerContactsSortedWithRemovableEmails = existingCustomerContacts?.data?.sort((_a, b) => {
  //   const contactName = b?.email || b?.name;
  //   // we have to keep all the removable contacts at the end of the list so that remove button contacts will always be at the end
  //   return removableCustomerEmailList?.includes(contactName) ? -1 : 1;
  // });
  // const accountIds = existingCustomerContactsSortedWithRemovableEmails?.map(({ account_id }) => account_id);
  // const accountEmails = existingCustomerContactsSortedWithRemovableEmails?.map(({ email, name }) => email || name);
  // const existingAccountIds = accountIds && accountIds.length > 0 ? accountIds : [''];
  // const existingAccountEmails = accountEmails && accountEmails.length > 0 ? accountEmails : [''];
  // const [atlassianAccountIds, setAtlassianAccountIds] = React.useState(existingAccountIds);
  // const [atlassianAccountEmails, setAtlassianAccountEmails] = React.useState<Array<string>>(existingAccountEmails);

  // insert default input field if no existing contact found required for the child component logic
  const [atlassianAccountIds, setAtlassianAccountIds] = React.useState<string[]>(['']);
  const [atlassianAccountEmails, setAtlassianAccountEmails] = React.useState<Array<string>>(['']);
  const [shouldShowBillCouplingWarning, setShouldShowBillCouplingWarning] = React.useState(false);
  const [eccLoading, setEccLoading] = useState<boolean>(true);
  const [existingECCInIG, setExistingECCInIG] = useState<Array<EndCustomerContact>>([]);
  const [removableContactList, setRemovableContactList] = useState<string[]>(removableCustomerEmailList);
  const [areEstimatesRefreshing, setAreEstimatesRefreshing] = React.useState(false);
  const [secondaryCustomerContacts, setSecondaryCustomerContacts] = React.useState<EndCustomerContact[]>([]);
  const [hasAddedNewPrimaryContact, setHasAddedNewPrimaryContact] = React.useState(false);
  const [hasAddedPrimaryContactInTheModal, setHasAddedPrimaryContactInTheModal] = React.useState(false);
  const isTransferOrder = currentOperationTypes?.includes(OperationType.TRANSFER);
  const [allSecondaryCustomerContacts, setAllSecondaryCustomerContacts] = React.useState<ContactInfo[]>([]);
  // for transfer case we are explicitly asking for confirm by checkbox
  const [agreedWithTnC, setAgreedWithTnC] = React.useState(!isTransferOrder && !isDesignIteration1Enabled);
  const [primaryContactsAaidsForBp, setPrimaryContactsAaidsForBp] = useState<Array<string>>(['']);
  const [primaryContactEmailsForBp, setPrimaryContactEmailsForBp] = useState<Array<string>>(['']);
  const [associatedPaymentMethodNull, setAssociatedPaymentMethodNull] = React.useState(false);
  const hasPrimaryContactPresent = useMemo(
    () =>
      isPrimaryContactsEnabled ? hasExistingPrimaryContact || atlassianAccountIds?.findIndex((id) => id) >= 0 : true,
    [atlassianAccountIds, hasExistingPrimaryContact, isPrimaryContactsEnabled]
  );

  let isDeactivatedBillingProfileSelected = false;

  const hasValidationErrors =
    !eccLoading &&
    !isBillingProfilesForCustomerLoading &&
    (!agreedWithTnC || associatedPaymentMethodNull || !hasPrimaryContactPresent);

  const [selectedBillingProfile, setSelectedBillingProfile] = useState<ValueType<BillingProfileOption> | null>(() =>
    invoiceGroupId
      ? {
          label: invoiceGroupName || '',
          value: invoiceGroupId,
        }
      : null
  );

  const {
    isOpen: showBillingProfileAndPONumberModal,
    open: openBillingProfileAndPONumberModal,
    close: closeBillingProfileAndPONumberModal,
  } = useOpenClose();

  const {
    isOpen: showChangePaymentMethodModal,
    open: openChangePaymentMethodModal,
    close: closeChangePaymentMethodModal,
  } = useOpenClose();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState<PaymentMethod | null>(null);
  const [isPaymentMethodLoading, setIsPaymentMethodLoading] = React.useState(false);
  const [billingProfileAction, setBillingProfileAction] = React.useState<BillingProfileActionsEnum>();
  const [isUpdatingPONumberFailed, setIsUpdatingPONumberFailed] = React.useState(false);
  const [updatingPONumber, setUpdatingPONumber] = React.useState<boolean>(false);

  const newSiteSelectedProduct = cartProducts.find((product) => product.cloudSiteType === CloudSiteTypeEnum.NEW);
  const totalAmountExcludingDiscountAndTaxes = lineItemsActualEstimate?.totalExcludingDiscountAndTax;
  const totalAmountToPay = lineItemsActualEstimate?.totalAmountToPay;
  const totalTaxPayable = lineItemsActualEstimate?.totalTax;
  const totalDiscountApplied = lineItemsActualEstimate?.totalDiscountApplied;

  let filteredBillingProfiles = availableBillingProfilesForCustomer;
  /* we're using this sorting because the response from api is ASCII sorted, so we need to sort it in a case-insensitive way.
   * This is a temporary solution, and we are only using it here since we are fetching all the billing profiles in one go.
   */
  filteredBillingProfiles = sortBillingProfiles(
    filteredBillingProfiles,
    true, // isSortEnabled - true
    true // showDisabledDeferredPaymentMethods - true, in other places it is passed as false
  );

  const selectedBillingProfileDetails = selectedBillingProfile
    ? availableBillingProfilesForCustomer?.find((invoiceGroup) => invoiceGroup?.id === selectedBillingProfile.value)
    : null;

  isDeactivatedBillingProfileSelected = selectedBillingProfileDetails?.isActive === false;

  const isPaypalBillingProfileSelected = isPaypalPaymentMethod(
    selectedBillingProfileDetails?.defaultPaymentMethod?.type
  );

  const currentPricingEligibility = (selectedBillingProfileDetails?.shipToPriceEligibility?.toUpperCase() ??
    CCPPricing.COMMERCIAL) as CCPPricing;
  const currentCurrency = isMultiCurrencyChangesEnabled
    ? selectedCurrency
    : ((selectedBillingProfileDetails?.currency?.toUpperCase() ?? Currency.USD) as Currency);
  const getOrderType = (lineItems: OrderSummaryLineItems) => {
    const orderType: string[] = [];
    lineItems.forEach((item) => {
      if (item.productStatuses) {
        item.productStatuses.forEach((status) => {
          if (!orderType.includes(status)) {
            orderType.push(status);
          }
        });
      }
      if (item.appLineItems.length > 0) {
        item.appLineItems.forEach((apps) => {
          const defaultAppProductStatuses = apps?.productStatuses ?? [];
          const appProductStatuses = selectedLinkedEntitlements?.[apps.id]?.productStatuses ?? []; // For apps use the updated status from selected app from store
          const appStatuses = uniqBy([...defaultAppProductStatuses, ...appProductStatuses], function (status) {
            return status;
          });
          (appStatuses ?? []).forEach((status) => {
            if (!orderType.includes(status)) {
              orderType.push(status);
            }
          });
        });
      }
    });

    return orderType;
  };

  const getSelectedProducts = (lineItems: OrderSummaryLineItems) => {
    //if any selected product has status new, edited, or transfer
    const index = lineItems.findIndex((item) => get(item, 'productStatuses', []).length > 0);
    return index >= 0;
  };

  const formatCustomerContactList = (existingContactsInIG: string[], existingContactsInSubscription: string[]) => {
    /**
     * If the number of contacts in the invoice group is already 5, then return the existing contacts in the invoice group
     * as we cannot add more than 5 contacts in the invoice group
     *
     * If the number of contacts in the invoice group is less than 5, then add the contacts from the subscription
     * which are not already present in the invoice group
     *
     */
    if (existingContactsInIG.length >= getMaxLimitOfCustomerContact()) {
      return existingContactsInIG;
    }

    // Below logic will handle when no contacts are present in the invoice group, this will still show blank contacts
    if (existingContactsInIG.length === 0) {
      return existingContactsInSubscription.slice(0, getMaxLimitOfCustomerContact());
    }

    const numberOfContactsToAdd = getMaxLimitOfCustomerContact() - existingContactsInIG.length;

    const contactsToAdd = existingContactsInSubscription
      .filter((contact) => !isEmpty(contact) && !existingContactsInIG.includes(contact))
      .slice(0, numberOfContactsToAdd);
    return [...existingContactsInIG, ...contactsToAdd];
  };

  React.useEffect(() => {
    (async () => {
      if (!hasAddedPrimaryContactInTheModal && invoiceGroupId && transactionAccountId) {
        setEccLoading(true);
        let data = await getECCDetails(invoiceGroupId, transactionAccountId);
        if (isPrimaryContactsEnabled) {
          const secondaryContacts = data?.filter((contact: ContactInfo) => !contact?.isPrimary);
          setSecondaryCustomerContacts(secondaryContacts);
          data = data?.filter((contact: ContactInfo) => contact?.isPrimary);
          data?.length > 0 && setHasExistingPrimaryContact(true);
        }
        setExistingECCInIG(data); // contains primary contacts if ecc-v3 change is enabled else contains all the customer contacts linked to the bp

        const existingContacts = data as EndCustomerContact[];
        const accountIds = existingContacts?.map(({ accountId }) => accountId);
        const accountEmails = existingContacts?.map(({ email, name }) => email || name);
        const existingAccountIdsFromContactsAPI = accountIds && accountIds.length > 0 ? accountIds : [];
        const existingAccountEmailsFromContactsAPI = accountEmails && accountEmails.length > 0 ? accountEmails : [];
        // Remove the contacts which are already present in the invoice group from removableCustomerEmailList
        const updatedRemovableCustomerEmailList = removableCustomerEmailList?.filter(
          (email) => !existingAccountEmailsFromContactsAPI.includes(email)
        );
        setRemovableContactList(updatedRemovableCustomerEmailList);
        setAtlassianAccountIds(formatCustomerContactList(existingAccountIdsFromContactsAPI, atlassianAccountIds));
        setAtlassianAccountEmails(
          formatCustomerContactList(existingAccountEmailsFromContactsAPI, atlassianAccountEmails)
        );
      }
      setEccLoading(false);
    })();
  }, [invoiceGroupId, hasAddedPrimaryContactInTheModal]);

  React.useEffect(() => {
    (async () => {
      if (availableBillingProfilesForCustomer && !isBillingProfilesForCustomerLoading && isPrimaryContactsEnabled) {
        const invoiceGroupIds = availableBillingProfilesForCustomer?.map((invoiceGroup) => invoiceGroup.id);
        const response = await getBulkECCDetails(invoiceGroupIds as string[], transactionAccountId);
        if (!isEmpty(response)) {
          const primaryContacts = response?.filter((contact: ContactInfo) => contact?.isPrimary);
          setPrimaryContacts(primaryContacts);
          const secondaryContacts = response?.filter((contact: ContactInfo) => !contact?.isPrimary);
          setAllSecondaryCustomerContacts(secondaryContacts);
        }
      }
    })();
  }, [availableBillingProfilesForCustomer, isBillingProfilesForCustomerLoading, isPrimaryContactsEnabled]);

  // For the pre selected invoice group id, we need to check if the payment method is null
  // if it is null, we need to show the error message
  React.useEffect(() => {
    if (!isBillingProfilesForCustomerLoading) {
      setAssociatedPaymentMethodNull(isPaymentMethodNullForSelectedBillingProfile);
    }
  }, [isBillingProfilesForCustomerLoading, isPaymentMethodNullForSelectedBillingProfile]);

  React.useEffect(() => {
    setShouldShowBillCouplingWarning(false);
    if (!selectedBillingProfile?.value) return;
    if (selectedBillingProfile?.value) {
      // here is the detailed document to explain about the bill coupled items https://hello.atlassian.net/wiki/spaces/COMITFO/pages/3269591945/Showing+bill+coupled+warning+banner
      const coupledItems = getBillCoupledItemsForIGChange({
        products,
        invoiceGroupId: selectedBillingProfile?.value,
        selectedProducts,
        cartApps,
      });
      if (coupledItems?.length > 0) {
        setShouldShowBillCouplingWarning(true);
      }
    }
  }, [selectedBillingProfile, selectedProducts, products]);

  React.useEffect(() => {
    if (isPrimaryContactsEnabled && selectedBillingProfile?.value) {
      const hasExistingPrimaryContact = primaryContacts?.find(
        (contact: ContactInfo) => contact?.identifierId === selectedBillingProfile?.value
      );
      setHasExistingPrimaryContact(!!hasExistingPrimaryContact);
    }
  }, [selectedBillingProfile?.value, primaryContacts]);

  React.useEffect(() => {
    if (
      secondaryCustomerContacts?.find((contact) => contact?.accountId === atlassianAccountIds[0]) ||
      allSecondaryCustomerContacts
        ?.filter((contact: any) => contact?.identifierId === selectedBillingProfile?.value)
        ?.find((contact: any) => contact?.accountId === atlassianAccountIds[0])
    ) {
      setHasAddedNewPrimaryContact(false);
    } else setHasAddedNewPrimaryContact(true);
  }, [atlassianAccountIds, secondaryCustomerContacts, invoiceGroupId]);

  const [hasOrderBeenPlaced, setHasOrderBeenPlaced] = React.useState(false);
  const [orderSlug, setOrderSlug] = React.useState('');
  const [newSiteOrderId, setNewSiteOrderId] = React.useState('');
  const updateConsentHub = async () => {
    // using "textContent" DOM API because it gives the text part and removes the link tags from HTML content
    const content = (Array.from(document.querySelectorAll('.js-consent-hub-text')) ?? []).reduce(
      (finalString, currentNode) => {
        finalString += currentNode.textContent;
        return finalString;
      },
      ''
    );
    await updateCustomerConsent(content);
  };

  const sendOrderFailureEvent = () => {
    sendAnalyticsEvent(AnalyticsTrackAction.FAILED, AnalyticsTrackSubject.ORDER);
  };

  const saveECCFailureMessage = () => {
    return <div>You can still update it in the billing profile section, or contact {getSupportUrl()}.</div>;
  };

  const contactServiceECCHandler = async () => {
    try {
      const existingAccountIds = existingECCInIG?.map((contact) => contact.accountId);
      const createContactIds = atlassianAccountIds?.filter((id) => !existingAccountIds?.includes(id));
      let createOrPatchContactPayload;
      if (isPrimaryContactsEnabled && !hasAddedNewPrimaryContact) {
        createOrPatchContactPayload = getPatchPayloadForSetPrimaryContact({
          invoiceGroupId: invoiceGroupId,
          transactionAccountId: transactionAccountId,
          primaryContactId: atlassianAccountIds[0],
        });
      } else {
        createOrPatchContactPayload = getCreateContactRelationshipsPayload({
          invoiceGroupId: invoiceGroupId,
          transactionAccountId: transactionAccountId,
          createContactIds: createContactIds,
          ...(isPrimaryContactsEnabled && { primaryContactId: atlassianAccountIds[0] }),
        });
      }
      if (!isEmpty(createOrPatchContactPayload)) {
        if (isPrimaryContactsEnabled && !hasAddedNewPrimaryContact) {
          await patchContactRelationship(createOrPatchContactPayload as PatchContactRelationshipsRequest);
        } else await createContactRelationship(createOrPatchContactPayload as CreateContactRelationshipsRequest);
      }
    } catch (err) {
      sendErrorNotification(
        errorNotificationBuilder()
          .withId('ecc-not-updated')
          .withHeader("We're unable to update customer contact")
          .withMessage(saveECCFailureMessage())
          .withDismissAfter(5000)
          .isVersion(NotificationTypes.FLAG)
          .build()
      );
    }
  };

  const renderValidationErrorPanel = () => {
    const errorMessages = [];

    if (associatedPaymentMethodNull) {
      errorMessages.push('You need a payment method.');
    }
    if (!hasPrimaryContactPresent && !eccLoading) {
      errorMessages.push('You need to add atleast one customer contact.');
    }
    if (!agreedWithTnC) {
      errorMessages.push('You must accept the terms and conditions.');
    }

    return (
      errorMessages.length > 0 && (
        <InfoPanel
          type="error"
          message={
            <div>
              {errorMessages.length === 1 ? (
                <>{errorMessages[0]}</>
              ) : (
                <ul>
                  {errorMessages.map((message, index) => (
                    <li key={index}>{message}</li>
                  ))}
                </ul>
              )}
            </div>
          }
        />
      )
    );
  };

  const handleSubmit = async (formState: { poNumber: string }) => {
    if (!cartProducts) return;

    setIsSubmitting(true);
    let isPreviewSuccessful = false;
    try {
      const omsPayload = await getOmsPayload({
        initialStoreProducts: products,
        offeringsAndpricingPlans,
        cartProducts,
        cartApps,
        nextBillDate,
        selectedProducts,
        transactionAccountId,
        invoiceGroupId,
        partnerPromotions,
        omsCapability: OmsCapability.ORDER_PREVIEW,
        endCustomerContacts: [],
        productsArr: Object.values(products),
        selectedLinkedEntitlements,
        isMultipleIgChangeHandlingForOrderEnabled: true,
        currentPricingEligibility,
        isIGChangeForFreeEntitlementEnabled,
        isECCInMetaDataDisabled: true,
        sandboxProducts: sandboxProducts,
        sandboxApps: sandboxApps,
        currentCurrency,
        isNonCommercialPricingEnabled,
        isJPDAsJiraFamilyEnabled,
        isCoTermInUofEnabled,
        isSandboxAppCreateInUOFEnabled,
        sandboxParentData,
        isAppEditionSupportEnabled,
      });

      const previewResponse = await getOrderPreviewV2(omsPayload);
      const items: orderItemResponse[] = previewResponse?.items ?? [];

      const itemsWithError = items.find((item) => !!item.errors);
      isPreviewSuccessful = !itemsWithError;

      if (isPreviewSuccessful) {
        // @ts-ignore
        await Promise.all([handleOnSavePONumber(formState.poNumber, { value: invoiceGroupId }), updateConsentHub()]);
        contactServiceECCHandler();

        const orderResponse = await updateEntitlementPlanV2(omsPayload);
        setHasOrderBeenPlaced(true);
        setOmsOrderResponse(orderResponse);
        const newSiteOrderResponse = orderResponse?.items.find(
          (item: any) => item.itemId === newSiteSelectedProduct?.id
        );
        setOrderDetails &&
          setOrderDetails({
            hasOrderBeenPlaced: true,
            newSiteOrderId: newSiteOrderResponse?.orderId ?? '',
            omsResponse: orderResponse,
          });
        //Sending event on successful order placement
        sendAnalyticsEvent(AnalyticsTrackAction.PLACED, AnalyticsTrackSubject.ORDER, {
          orderId: orderResponse.orderId,
          orderTypes: getOrderType(lineItems),
          partnerAccount: relatedPartnerAccount?.name,
          isProductsSelected: getSelectedProducts(lineItems),
          isAppsSelected: !isEmpty(selectedLinkedEntitlements),
        });

        const getApps = (appLineItems: OrderSummaryAppLineItem[]) => {
          const linkedEntitlements: string[] = [];
          appLineItems.forEach((app) => {
            linkedEntitlements.push(app.name);
          });

          return linkedEntitlements;
        };
        lineItems?.forEach((element) => {
          sendAnalyticsEvent(AnalyticsTrackAction.PLACED, PPCAnalyticsTrackSubject.APP_OR_PRODUCT_ORDER, {
            productName: element.name,
            appName: element.appLineItems && getApps(element.appLineItems),
            status: element.productStatuses && element.productStatuses[0],
          });
          // for tracking new product purchases
          if (isEqual(element.productStatuses, [ProductStatus.NEW])) {
            sendAnalyticsEvent(AnalyticsTrackAction.PLACED, PPCAnalyticsTrackSubject.NEW_APP_OR_PRODUCT_ORDER, {
              name: element.name,
              isProduct: true,
            });
          }
          // for tracking new app purchases
          if (element.appLineItems) {
            element.appLineItems.forEach((apps) => {
              if (isEqual(apps.productStatuses, [ProductStatus.NEW])) {
                sendAnalyticsEvent(AnalyticsTrackAction.PLACED, PPCAnalyticsTrackSubject.NEW_APP_OR_PRODUCT_ORDER, {
                  name: apps.name,
                  isApp: true,
                });
              }
            });
          }
        });
        setCurrentScreen(CurrentScreenTypeEnum.ORDER_CONFIRMATION);
        setOrderSlug(orderResponse?.slug ?? '');
        setNewSiteOrderId(newSiteOrderResponse?.orderId ?? '');
        setSubmitError(null);
      } else {
        // as CCP only returns `itemId` along with validation error response, we need to enrich it with `entitlementId` so that the errors can be mapped back to the actual cart product.
        const validationErrors: ValidationErrorSchema[] = items
          .filter((item) => !!item.errors)
          .map((item) => ({
            ...item,
            entitlementId: getEntitlementIdForItemId(omsPayload, item.itemId),
          }));

        setValidationErrors(validationErrors);

        setSubmitError({
          description:
            'There are some issues with your order that prevented us from placing it. Please correct them and retry.',
        });
      }
    } catch (err: any) {
      // sending analytics event to track order failure
      if (isPreviewSuccessful) {
        sendOrderFailureEvent();
      }

      const ccpErrorCode = err?.response?.data?.details?.[0]?.code;

      const formattedErrorMessage = formatErrorMessage(ccpErrorCode);
      setIsSubmitting(false);
      setSubmitError(
        formattedErrorMessage
          ? formattedErrorMessage
          : { description: `Unable to place your order. ${(err && err.message) ?? ''}` }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getErrorDescription = (description: string) => {
    if (!isEmpty(description)) {
      return (
        <div>
          {description} Contact {getSupportUrl()} for help.
        </div>
      );
    }
    return <div>Contact {getSupportUrl()} for help.</div>;
  };

  const errorToasts = (
    <FlagGroup onDismissed={() => setSubmitError(null)}>
      {submitError && (
        <AutoDismissFlag
          icon={<WarningIcon label="Warning" primaryColor={'red'} />}
          id={submitError.description}
          title={submitError.title || 'Save failed'}
          description={getErrorDescription(submitError.description)}
        />
      )}
    </FlagGroup>
  );

  const getDisclaimerMessage = () => {
    const pmsLink = `https://admin.atlassian.com/partner-managed-subscriptions`;
    const privacyLink = `https://www.atlassian.com/legal/privacy-policy#what-this-policy-covers`;
    const customerAgreementUrl = 'https://www.atlassian.com/legal/atlassian-customer-agreement#intro';

    const agreementConfirmationText = () => (
      <>
        By confirming the order, and in accordance with the order section of the partner agreement executed between You
        and Atlassian, you agree and acknowledge to be bound by the partner agreement, and acknowledge the{' '}
        <a data-testid="atlassian-privacy-link" href={privacyLink} target="_blank" rel="noreferrer">
          Privacy Policy
        </a>
        .
      </>
    );

    const ensureCustomerAgreementCompliance = () => (
      <>
        You are responsible for ensuring each customer has entered into a customer agreement, at or before such
        customer&apos;s purchase or use of the offerings, in a manner that is legally binding upon the customer.
      </>
    );

    const agreementConflictResolutionText = () => (
      <>
        Except where you have a current agreement with Atlassian, the terms of such agreement shall supersede the{' '}
        <a href={customerAgreementUrl} target="_blank" rel="noreferrer">
          Atlassian Customer Agreement.
        </a>{' '}
        In the event of any conflict between the terms of your current agreement and the{' '}
        <a href={customerAgreementUrl} target="_blank" rel="noreferrer">
          Atlassian Customer Agreement
        </a>
        , the terms of your current agreement shall prevail.
      </>
    );

    return (
      <>
        <p className="order-info mb-16">
          {agreementConfirmationText()} {agreementConflictResolutionText()}
        </p>
        <p className="order-info mb-16"> {ensureCustomerAgreementCompliance()}</p>
        <p className="order-info mb-16">
          You acknowledge that your order contains monthly auto-renewal subscriptions. You agree to the details, price,
          frequency, and other adjustments presented here, and understand that if your customer adjusts the number of
          users, the price will be adjusted accordingly. On the next bill date and after any trial periods, we&apos;ll
          invoice you with the final amount. You may first be charged a prorated amount to align your bill date with
          other subscriptions.
        </p>
        <p className="order-info mb-16">
          Our prices may change upon notice to you. Your customer contact can cancel the subscription anytime by going
          to{' '}
          <a data-testid="atlassian-pms-link" href={pmsLink} target="_blank" rel="noreferrer">
            admin.atlassian.com/partner-managed-subscriptions
          </a>
          .
        </p>
        {isTransferOrder ? (
          <p className="order-info mb-16">
            Subscriptions on the same site that belong to the same product family will be also automatically
            transferred.
          </p>
        ) : null}
      </>
    );
  };

  const getCheckBoxLabel = () => {
    return (
      <>
        <span>The customer has consented to moving the subscriptions to your billing account.</span>
      </>
    );
  };
  const isAllEndCustomerContactAdded = React.useMemo(() => !(atlassianAccountIds.findIndex((id) => !id) >= 0), [
    atlassianAccountIds,
  ]);

  const isOrderConfirmationDisabled = React.useMemo(
    () =>
      !invoiceGroupId ||
      !agreedWithTnC ||
      (!isPrimaryContactsEnabled && !isCustomerContactHidden && !isAllEndCustomerContactAdded) ||
      associatedPaymentMethodNull ||
      isDeactivatedBillingProfileSelected ||
      !hasPrimaryContactPresent ||
      // disable the confirmation button in case fetching of billing profiles for customer fails
      !!fetchBillingProfilesForCustomerError ||
      (!isSupportForPayPalEnabled && isPaypalBillingProfileSelected) ||
      !!isBillingProfilesForCustomerLoading,
    [
      invoiceGroupId,
      agreedWithTnC,
      isAllEndCustomerContactAdded,
      associatedPaymentMethodNull,
      isDeactivatedBillingProfileSelected,
      fetchBillingProfilesForCustomerError,
      isSupportForPayPalEnabled,
      isPaypalBillingProfileSelected,
      isCustomerContactHidden,
      hasPrimaryContactPresent,
      isBillingProfilesForCustomerLoading,
    ]
  );

  const isNewOrderConfirmationDisabled = React.useMemo(
    () => !invoiceGroupId || !!fetchBillingProfilesForCustomerError || !!isBillingProfilesForCustomerLoading,
    [invoiceGroupId, fetchBillingProfilesForCustomerError, isBillingProfilesForCustomerLoading]
  );

  const handleOnBillingProfileChange = () => {
    setBillingProfileAction(BillingProfileActionsEnum.CHANGE_BILLING_PROFILE);
    setIsUpdatingPONumberFailed(false);
    openBillingProfileAndPONumberModal();
  };

  const handleOnPONumberChange = () => {
    setBillingProfileAction(BillingProfileActionsEnum.CHANGE_PO_NUMBER);
    setIsUpdatingPONumberFailed(false);
    openBillingProfileAndPONumberModal();
  };

  const billingProfileActions: MoreActionsItem[] = [
    {
      id: 'po_number',
      content: 'PO number',
      isDisabled: associatedPaymentMethodNull,
      action: () => handleOnPONumberChange(),
    },
    {
      id: 'change_billing_profile',
      content: 'Switch',
      action: () => handleOnBillingProfileChange(),
    },
  ];

  const BillingProfileMoreActions = <MoreActions actions={billingProfileActions} />;

  const labelBillingProfile = () => {
    return isDesignIteration1Enabled ? (
      <label>
        <Heading size="medium">Billing profile details</Heading>
      </label>
    ) : (
      <label className="billing-profile-container-text-label">Billing profile</label>
    );
  };

  const labelBillingAddress = React.useMemo(() => {
    return isDesignIteration1Enabled ? (
      <label>
        <Heading size="medium">Billing address</Heading>
      </label>
    ) : (
      <label className="order-review-container-text-label">Billing address</label>
    );
  }, [isDesignIteration1Enabled]);

  const handleOnSavePONumber = async (PONumber: string, selectedBillingProfile: ValueType<BillingProfileOption>) => {
    if (!PONumber) {
      closeBillingProfileAndPONumberModal();
      return 'success';
    }
    setUpdatingPONumber(true);
    const billingProfileDetails = filteredBillingProfiles?.find(
      (billingProfile) => billingProfile.id === selectedBillingProfile?.value
    );
    if (selectedBillingProfile?.value && billingProfileDetails) {
      try {
        const requestData: UpdateInvoiceGroupRequest = {
          data: {
            name: billingProfileDetails.name,
            shipToPartyId: billingProfileDetails.shipToParty?.id,
            purchaseOrder: {
              number: PONumber,
              oneTimeUse: false,
            },
          },
          invoiceGroupId: selectedBillingProfile?.value,
          transactionAccountId: transactionAccountId,
        };
        const result = await updateInvoiceGroup(requestData);
        setIsUpdatingPONumberFailed(false);
        closeBillingProfileAndPONumberModal();
        refetchbillingProfilesForCustomer();
        return 'success';
      } catch (e) {
        setIsUpdatingPONumberFailed(true);
      } finally {
        setUpdatingPONumber(false);
      }
    }
    return 'failed';
  };

  const checkForAssociatedPaymentMethod = (selectInvoiceGroupID: any) => {
    let billingProfileDetail;
    if (selectInvoiceGroupID) {
      billingProfileDetail = availableBillingProfilesForCustomer.find(
        (billingProfileDetail) => selectInvoiceGroupID === billingProfileDetail?.id
      );
    }
    return billingProfileDetail?.defaultPaymentMethod;
  };

  const showBillingProfileSelectedMessage = () => {
    if (notificationSent) return;

    const notificationOptions = successNotificationBuilder()
      .withId('billing-profile-selected')
      .withHeader('Billing profile selected')
      .withDismissAfter(5000)
      .isVersion(NotificationTypes.FLAG)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  };

  const handleOnSaveBillingProfile = async (
    selectedBillingProfile: ValueType<BillingProfileOption>,
    PONumber: string,
    atlassianAccountIds?: string[],
    atlassianAccountEmails?: string[],
    hasAddedPrimaryContactInTheModal?: boolean
    /* eslint-disable max-depth */
    /* eslint-disable max-params */
  ) => {
    if (selectedBillingProfile?.value) {
      const isPaymentMethodNullForIG = !checkForAssociatedPaymentMethod(selectedBillingProfile?.value);
      if (!isPaymentMethodNullForIG) {
        if (!(billingProfileAction === BillingProfileActionsEnum.CHANGE_BILLING_PROFILE)) {
          const result = await handleOnSavePONumber(PONumber, selectedBillingProfile);
          if (result === 'success') {
            if (isPrimaryContactsEnabled) {
              atlassianAccountIds && setAtlassianAccountIds(atlassianAccountIds);
              atlassianAccountEmails && setAtlassianAccountEmails(atlassianAccountEmails);
              setHasAddedPrimaryContactInTheModal(!!hasAddedPrimaryContactInTheModal);
            }
            setSelectedBillingProfile(selectedBillingProfile);
            setInvoiceGroupId(selectedBillingProfile?.value);
            setAssociatedPaymentMethodNull(false);
            closeBillingProfileAndPONumberModal();
            showBillingProfileSelectedMessage();
          }
        } else if (billingProfileAction === BillingProfileActionsEnum.CHANGE_BILLING_PROFILE) {
          if (isPrimaryContactsEnabled) {
            atlassianAccountIds && setAtlassianAccountIds(atlassianAccountIds);
            atlassianAccountEmails && setAtlassianAccountEmails(atlassianAccountEmails);
            setHasAddedPrimaryContactInTheModal(!!hasAddedPrimaryContactInTheModal);
          }
          setSelectedBillingProfile(selectedBillingProfile);
          setInvoiceGroupId(selectedBillingProfile?.value);
          setAssociatedPaymentMethodNull(false);
          closeBillingProfileAndPONumberModal();
          showBillingProfileSelectedMessage();
        }
      } else {
        if (isPrimaryContactsEnabled) {
          atlassianAccountIds && setAtlassianAccountIds(atlassianAccountIds);
          atlassianAccountEmails && setAtlassianAccountEmails(atlassianAccountEmails);
          setHasAddedPrimaryContactInTheModal(!!hasAddedPrimaryContactInTheModal);
        }
        setSelectedBillingProfile(selectedBillingProfile);
        setInvoiceGroupId(selectedBillingProfile?.value);
        setAssociatedPaymentMethodNull(true);
        closeBillingProfileAndPONumberModal();
      }
    }
  };

  const handleBillingProfileCancel = useCallback(async (hasAddedPrimaryContactInTheModal?: boolean) => {
    isPrimaryContactsEnabled &&
      hasAddedPrimaryContactInTheModal &&
      setHasAddedPrimaryContactInTheModal(hasAddedPrimaryContactInTheModal);
    closeBillingProfileAndPONumberModal();
  }, []);

  const getConfirmationScreen = () => {
    if (isDesignIteration1Enabled) {
      setSummaryDrawerOpen(false);
    }
    return (
      <OrderConfirmationContainer
        newSiteOrderId={newSiteOrderId}
        transactionAccountId={transactionAccountId}
        orderResponse={omsOrderResponse}
        onTryAgainClick={() => {
          setSummaryDrawerOpen(false);
          onResetOrder && onResetOrder();
        }}
      />
    );
  };

  const renderECCFailureMessage = () => {
    return <div>Visit the order summary screen or contact {getSupportUrl()}.</div>;
  };

  const showECCFetchFailedMessage = () => {
    if (notificationSent) {
      return;
    }
    const notificationOptions = errorNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('customer-contact-fetch-error')
      .withHeader("We're unable to load customer contacts")
      .withMessage(renderECCFailureMessage())
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  };

  if (eccDetailsError) {
    showECCFetchFailedMessage();
  }

  const getLinkedEntitlementInfoPanelMessage = () => {
    if (isSandboxLinkedEntitlementsInCart && isRovoEntilementsInCart) {
      return 'Any Rovo instances and sandboxes linked to subscriptions being transferred will also be transferred.';
    }
    if (isSandboxLinkedEntitlementsInCart) {
      return 'Any sandboxes linked to subscriptions being transferred will also be transferred.';
    }
    if (isRovoEntilementsInCart) {
      return 'Any Rovo instances linked to subscriptions being transferred will also be transferred.';
    }
    return '';
  };

  const billingAddressContent = useMemo(() => {
    return (
      <Box xcss={isDesignIteration1Enabled ? billingAddressStyle : null}>
        <div className={classnames({ 'commerce-address-container': !isDesignIteration1Enabled })}>
          {labelBillingAddress}
          <div
            className={classnames('selected-commerce-address-wrapper', {
              'mt-16 selected-commerce-address-wrapper-v2': isDesignIteration1Enabled,
            })}
          >
            <FormatCommerceAddress commerceAddress={billToAddressData} />
          </div>
        </div>
      </Box>
    );
  }, [billToAddressData]);

  const billingProfileContainer = (() => {
    return (
      <>
        {selectedBillingProfile && !fetchBillingProfilesForCustomerError ? (
          <>
            <BillingProfile
              isLoading={isBillingProfilesForCustomerLoading}
              billingProfileInfo={selectedBillingProfileDetails ?? billingProfileDetails}
              selectedPaymentMethod={selectedPaymentMethod}
              isPaymentMethodLoading={isPaymentMethodLoading}
              invoiceGroupName={selectedBillingProfileDetails?.name || billingProfileDetails?.name}
              Label={labelBillingProfile}
              BillingProfileMoreActions={BillingProfileMoreActions}
              displayPONumber={true}
              shouldShowBillCouplingWarning={shouldShowBillCouplingWarning}
              atlassianAccountIds={atlassianAccountIds}
              setAtlassianAccountIds={setAtlassianAccountIds}
              atlassianAccountEmails={atlassianAccountEmails}
              setAtlassianAccountEmails={setAtlassianAccountEmails}
              eccLoading={eccLoading}
              isAddNewContactDisabled={atlassianAccountIds.length >= getMaxLimitOfCustomerContact()}
              isExistingContactRemoveDisabled={true}
              currentUserList={atlassianAccountIds}
              removableCustomerEmailList={removableContactList}
              showPricingEligibility={isNonCommercialPricingEnabled}
              showPrimaryCustomerContacts={isPrimaryContactsEnabled}
              secondaryCustomerContacts={allSecondaryCustomerContacts}
              isBulkECCDetailsLoading={bulkEccDetailsLoading}
              isMultiCurrencyChangesEnabled={isMultiCurrencyChangesEnabled}
              onEditPaymentClick={() => {
                openChangePaymentMethodModal();
              }}
              isDesignIteration1Enabled={isDesignIteration1Enabled}
              shouldShowACHPaymentMethod={isAchPaymentMethodFFEnabled}
            />
            {!isDesignIteration1Enabled && associatedPaymentMethodNull && (
              <ErrorMessage>{'Payment method missing in the selected billing profile'}</ErrorMessage>
            )}
          </>
        ) : (
          <>
            {fetchBillingProfilesForCustomerError ? (
              <div>
                <div className="add-billing-profile">
                  <label className="add-billing-profile-label">Billing profile</label>
                  <Button
                    appearance="primary"
                    testId="refetch-billing-profile-btn"
                    className="add-billing-profile-retry-btn"
                    onClick={() => {
                      refetchbillingProfilesForCustomer();
                    }}
                  >
                    Retry
                  </Button>
                </div>
              </div>
            ) : isBillingProfilesForCustomerLoading || (isPrimaryContactsEnabled && bulkEccDetailsLoading) ? (
              <SkeletonLoaderWrapper>
                <SkeletonLoader width="542px" height="156px" marginBottom={'0'} />
              </SkeletonLoaderWrapper>
            ) : (
              <div>
                <div className="add-billing-profile">
                  <label className="add-billing-profile-label">Billing profile</label>
                  <Button
                    testId="select-billing-profile-btn"
                    className="add-billing-profile-btn"
                    onClick={() => {
                      setBillingProfileAction(BillingProfileActionsEnum.NEW_BILLING_PROFILE);
                      openBillingProfileAndPONumberModal();
                    }}
                  >
                    Select billing profile
                  </Button>
                </div>
              </div>
            )}
          </>
        )}

        {isDeactivatedBillingProfileSelected && !isBillingProfilesForCustomerLoading && (
          <div className="error-section-message">
            <SectionMessage title={`We're unable to use this billing profile`} appearance="error">
              {'This billing profile is deactivated. To place the order, switch to a valid billing profile.'}
            </SectionMessage>
          </div>
        )}
      </>
    );
  })();

  const warningMessageForEntitlementsInCart = useMemo(() => {
    return isSandboxLinkedEntitlementsInCart || isRovoEntilementsInCart ? (
      <div className="order-review-container-sandbox-entitlements">
        <InfoPanel type="warning" message={getLinkedEntitlementInfoPanelMessage()} />
      </div>
    ) : null;
  }, [isSandboxLinkedEntitlementsInCart, isRovoEntilementsInCart]);

  return (
    <div className="order-review-container" data-testid="ppc-order-summary">
      {errorToasts}
      {hasOrderBeenPlaced ? (
        <>{getConfirmationScreen()}</>
      ) : (
        <>
          <div
            className={classnames('order-review-container-summary-header', {
              'mt-8 width-95': isDesignIteration1Enabled,
            })}
          >
            {isDesignIteration1Enabled ? <Heading size="large">Review order details</Heading> : <h1>Order summary</h1>}
            <p className="order-review-container-summary-header-sub">
              Review the subscription and billing details before confirming your order.
            </p>
          </div>
          <div
            className={classnames('order-review-container-review-summary', {
              'order-review-container-review-summary-v2': isDesignIteration1Enabled,
            })}
          >
            <div
              className={classnames('order-review-container-review-summary-details', {
                'gap-24 mt-8 width-95 full-height': isDesignIteration1Enabled,
              })}
            >
              <div
                className={classnames('order-review-container-review-summary-details-line-items-container', {
                  'width-60 pr-0 pb-50': isDesignIteration1Enabled,
                })}
              >
                <div
                  className={classnames(
                    'order-review-container-review-summary-details-line-items-container-line-items',
                    { 'pr-0': isDesignIteration1Enabled }
                  )}
                >
                  <>
                    {Object.entries(groupLineItemsByOrganisationName).map(([organisationName, lineItems], index) => {
                      /** currently atlassian access is the only product under org which is a non-jira product
                       * but keeping jira and non-jira lineitems separate for potential inclusion of jira items
                       * under organisation in future.
                       */
                      const jiraLineItems = lineItems.filter((lineItem) => lineItem.isItAJiraProduct);
                      const nonJiraLineItems = lineItems.filter((lineItem) => !lineItem.isItAJiraProduct);
                      return (
                        <CustomPanel
                          heading={organisationName}
                          toggleButtonOnLeft={true}
                          key={organisationName}
                          isDefaultExpanded={index === 0}
                          className="order-summary-custom-panel"
                          iconBefore={<OfficeBuildingFilledIcon label="" />}
                        >
                          <LineItems
                            lineItems={jiraLineItems}
                            nextBillDate={nextBillDate}
                            isJiraFamily={true}
                            loading={areEstimatesRefreshing}
                            isTWCPhase2Enabled={isTWCPhase2Enabled}
                            showSandboxStatus={showSandboxStatus}
                            selectedCurrency={currentCurrency}
                            {...orgProductInstancesMapProp}
                          />
                          {jiraLineItems && jiraLineItems.length > 0 && nonJiraLineItems.length > 0}
                          <LineItems
                            lineItems={nonJiraLineItems}
                            nextBillDate={nextBillDate}
                            isJiraFamily={false}
                            loading={areEstimatesRefreshing}
                            showSandboxStatus={showSandboxStatus}
                            selectedCurrency={currentCurrency}
                            isTWCPhase2Enabled={isTWCPhase2Enabled}
                            {...orgProductInstancesMapProp}
                          />
                        </CustomPanel>
                      );
                    })}
                    {Object.entries(groupedLineItemsByCloudSite).map(([existingCloudSite, lineItems], index) => {
                      const jiraLineItems = lineItems.filter((lineItem) => lineItem.isItAJiraProduct);
                      const nonJiraLineItems = lineItems.filter((lineItem) => !lineItem.isItAJiraProduct);
                      const cloudSiteNode = !existingCloudSite ? <NewCloudSiteLozenge /> : existingCloudSite;
                      return (
                        <CustomPanel
                          heading={cloudSiteNode}
                          toggleButtonOnLeft={true}
                          key={existingCloudSite}
                          isDefaultExpanded={index === 0}
                          className="order-summary-custom-panel"
                        >
                          <LineItems
                            lineItems={jiraLineItems}
                            nextBillDate={nextBillDate}
                            isJiraFamily={true}
                            loading={areEstimatesRefreshing}
                            showSandboxStatus={showSandboxStatus}
                            selectedCurrency={currentCurrency}
                            {...orgProductInstancesMapProp}
                          />
                          {jiraLineItems && jiraLineItems.length > 0 && nonJiraLineItems.length > 0}
                          <LineItems
                            lineItems={nonJiraLineItems}
                            nextBillDate={nextBillDate}
                            isJiraFamily={false}
                            loading={areEstimatesRefreshing}
                            showSandboxStatus={showSandboxStatus}
                            selectedCurrency={currentCurrency}
                            {...orgProductInstancesMapProp}
                          />
                        </CustomPanel>
                      );
                    })}
                  </>
                </div>
                {!isDesignIteration1Enabled ? (
                  <div className="order-review-container-review-summary-details-line-items-container-total-estimate-summary">
                    <div className="order-review-container-row-container">
                      <h5 className="order-review-container-review-summary-details-line-items-container-total-estimate-summary-total-estimate-label">
                        Total excl. tax
                      </h5>
                      <Estimate
                        currency={currentCurrency}
                        price={totalAmountExcludingDiscountAndTaxes}
                        isTotal={true}
                        loading={areEstimatesRefreshing}
                        className={
                          'order-review-container-review-summary-details-line-items-container-total-estimate-summary-total-estimate-currency'
                        }
                      />
                    </div>
                    {!areAllTheAddedProductsFree && (
                      <div className="order-review-container-row-container">
                        <div className="order-review-container-row-container tax-gap">
                          <p className="selected-app-pricing-details">Tax estimate</p>
                        </div>
                        <p className="selected-app-pricing-details"></p>
                        <div className="order-review-container-row-container tax-gap">
                          <Estimate
                            currency={currentCurrency}
                            price={totalTaxPayable}
                            className={
                              'order-review-container-review-summary-details-line-items-container-total-estimate-summary-sub-title-pricing-currency'
                            }
                            loading={areEstimatesRefreshing}
                          />
                        </div>
                      </div>
                    )}
                    {lineItemsActualEstimate?.totalAdminFee !== undefined && (
                      <div className="order-review-container-row-container">
                        <div className="order-review-container-row-container tax-gap">
                          <p className="selected-app-pricing-details">Total administration fee</p>
                        </div>
                        <p className="selected-app-pricing-details"></p>
                        <div className="order-review-container-row-container tax-gap">
                          <Estimate
                            currency={currentCurrency}
                            price={lineItemsActualEstimate?.totalAdminFee}
                            className={
                              'order-review-container-review-summary-details-line-items-container-total-estimate-summary-sub-title-pricing-currency'
                            }
                            loading={areEstimatesRefreshing}
                          />
                        </div>
                      </div>
                    )}
                    {lineItemsActualEstimate?.totalDiscountApplied !== undefined && (
                      <div className="order-review-container-row-container">
                        <div className="order-review-container-row-container tax-gap">
                          <p className="selected-app-pricing-details">Total discount</p>
                        </div>
                        <p className="selected-app-pricing-details"></p>
                        <div className="order-review-container-row-container tax-gap">
                          <Estimate
                            currency={currentCurrency}
                            price={lineItemsActualEstimate?.totalDiscountApplied}
                            className={
                              'order-review-container-review-summary-details-line-items-container-total-estimate-summary-sub-title-pricing-currency'
                            }
                            loading={areEstimatesRefreshing}
                          />
                        </div>
                      </div>
                    )}
                    <div className="order-review-container-row-container">
                      <p className="selected-app-pricing-details">Partner discount estimate</p>
                      <p className="selected-app-pricing-details"></p>
                      <Estimate
                        currency={currentCurrency}
                        price={-(totalDiscountApplied ?? 0)}
                        className={
                          'order-review-container-review-summary-details-line-items-container-total-estimate-summary-sub-title-pricing-currency'
                        }
                        loading={areEstimatesRefreshing}
                      />
                    </div>
                    <Divider className="mt-16 mb-16" />
                    <div className="order-review-container-row-container">
                      <h5 className="order-review-container-review-summary-details-line-items-container-total-estimate-summary-grand-total-estimate">
                        Total billed today
                      </h5>
                      <Estimate
                        currency={currentCurrency}
                        price={totalAmountToPay}
                        isTotal={true}
                        loading={areEstimatesRefreshing}
                        className={
                          'order-review-container-review-summary-details-line-items-container-total-estimate-summary-grand-total-currency'
                        }
                      />
                    </div>
                    {/* <div className="order-review-container-row-container">
                    <p>
                      Actual amount charged may be different if the number of users has changed by the end of the trial
                      period.
                    </p>
                  </div> */}
                  </div>
                ) : null}
              </div>
              <div
                className={classnames('place-order-actions-container', {
                  'width-40 relative': isDesignIteration1Enabled,
                })}
              >
                {!isBillingProfilesForCustomerLoading && (
                  <BillingProfileAndPONumberModal
                    isOpen={showBillingProfileAndPONumberModal}
                    onClose={handleBillingProfileCancel}
                    preSelectedBillingProfile={selectedBillingProfileDetails}
                    billingProfileAction={billingProfileAction}
                    billingProfilesInfo={filteredBillingProfiles}
                    isUpdatingPONumberFailed={isUpdatingPONumberFailed}
                    setIsUpdatingPONumberFailed={setIsUpdatingPONumberFailed}
                    updatingPONumber={updatingPONumber}
                    onSavePONumber={handleOnSavePONumber}
                    onSave={handleOnSaveBillingProfile}
                    // Below is passed false for Quote, hence cannot remove the prop
                    isDeferredPaymentMethodDisabled={true}
                    isNoPaymentMethodDisabled={true}
                    showPricingEligibility={isNonCommercialPricingEnabled}
                    shouldAllowBillingProfileTransferBasedOnPricingEligibility={isNonCommercialPricingEnabled}
                    allowedPricingEligibilityForBillingProfileTransfer={
                      selectedPricingEligibility ? [selectedPricingEligibility] : []
                    }
                    showPrimaryContacts={isPrimaryContactsEnabled}
                    primaryContacts={primaryContacts}
                    isPayPalEnabled={isSupportForPayPalEnabled}
                    secondaryCustomerContacts={allSecondaryCustomerContacts as any}
                    atlassianAccountIds={primaryContactsAaidsForBp}
                    setAtlassianAccountIds={setPrimaryContactsAaidsForBp}
                    atlassianAccountEmails={primaryContactEmailsForBp}
                    setAtlassianAccountEmails={setPrimaryContactEmailsForBp}
                    hasAddedPrimaryContactInTheModal={hasAddedPrimaryContactInTheModal}
                  />
                )}
                {isMultiCurrencyChangesEnabled && invoiceGroupId && (
                  <ChangePaymentMethodModal
                    isOpen={showChangePaymentMethodModal}
                    onClose={closeChangePaymentMethodModal}
                    invoiceGroupId={invoiceGroupId}
                    transactionAccountId={transactionAccountId}
                    onSuccess={() => {
                      setAssociatedPaymentMethodNull(false);
                      closeChangePaymentMethodModal();
                    }}
                    setSelectedPaymentMethod={setSelectedPaymentMethod}
                    setIsPaymentMethodLoading={setIsPaymentMethodLoading}
                    isPaypalSupportEnabled={isSupportForPayPalEnabled}
                    shouldShowACHPaymentMethod={isAchPaymentMethodFFEnabled}
                  />
                )}
                <Form onSubmit={handleSubmit}>
                  {({ formProps }) => {
                    return (
                      <form {...formProps}>
                        <div
                          className={classnames('place-order-actions-container-actions', {
                            'place-order-actions-container-actions-v2': isDesignIteration1Enabled,
                          })}
                        >
                          {isDesignIteration1Enabled ? (
                            <>
                              {warningMessageForEntitlementsInCart}
                              <Box xcss={totalEstimateStyle}>
                                <OrderSummaryTotalEstimate
                                  loading={areEstimatesRefreshing}
                                  currency={currentCurrency}
                                  isSubmitting={isSubmitting}
                                  isOrderConfirmationDisabled={isOrderConfirmationDisabled}
                                  breakup={{
                                    totalDiscount: lineItemsActualEstimate?.totalDiscountApplied ?? 0,
                                    totalAmount: totalAmountToPay,
                                    partnerDiscount: totalDiscountApplied,
                                    taxPayable: totalTaxPayable,
                                    adminFee: lineItemsActualEstimate?.totalAdminFee ?? 0,
                                  }}
                                  isTransferOrder={isTransferOrder}
                                  onAgreeToTnC={(state) => setAgreedWithTnC(state)}
                                  setSummaryDrawerOpen={setSummaryDrawerOpen}
                                  hasValidationErrors={hasValidationErrors}
                                  renderValidationErrorPanel={renderValidationErrorPanel}
                                />
                              </Box>
                              {billingProfileContainer}
                              {billingAddressContent}
                            </>
                          ) : null}

                          {!areAllTheAddedProductsFree && !isDesignIteration1Enabled && (
                            <div
                              className={`place-order-actions-container-order-info-selects`}
                              data-testid="ppc-order-info-selects-container"
                            >
                              {billingAddressContent}
                              {warningMessageForEntitlementsInCart}
                              {billingProfileContainer}
                              <div className="actions-container">
                                <div className="js-consent-hub-text">
                                  {!areAllTheAddedProductsFree && getDisclaimerMessage()}
                                  {isTransferOrder ? (
                                    <CheckboxField name="ppc-order-review-tnc" isRequired={true}>
                                      {({ fieldProps }) => (
                                        <Checkbox
                                          {...fieldProps}
                                          label={getCheckBoxLabel()}
                                          isChecked={agreedWithTnC}
                                          onChange={() => {
                                            setAgreedWithTnC((prevState) => {
                                              return !prevState;
                                            });
                                          }}
                                          testId={'tnc-checkbox'}
                                        />
                                      )}
                                    </CheckboxField>
                                  ) : null}
                                </div>
                                <div className="actions-footer">
                                  <AdvocateAccessWall
                                    accessType={ADVOCATE_ACCESS_TYPE.CREATE}
                                    resource={ADVOCATE_RESOURCE_TYPE.ORDER}
                                  >
                                    <LoadingButton
                                      type="submit"
                                      appearance="primary"
                                      testId={'ppc-order-review.place-order'}
                                      isLoading={isSubmitting}
                                      isDisabled={isOrderConfirmationDisabled}
                                    >
                                      Confirm
                                    </LoadingButton>
                                  </AdvocateAccessWall>
                                  <Button appearance="subtle" onClick={() => setSummaryDrawerOpen(false)}>
                                    Back
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </form>
                    );
                  }}
                </Form>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
